import { useFormValidation } from "@react-aria/form";
import { useFormValidationState } from "@react-stately/form";
import React, { type ChangeEvent, useCallback, useRef, useState } from "react";
import { VisuallyHidden } from "react-aria";
import {
    FieldError,
    FieldErrorContext,
    Input,
    InputContext,
    Label,
    Text,
} from "react-aria-components";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ButtonPrimary } from "../Clickables";
import { InputProps } from "./Input";

import styles from "./index.module.scss";

interface Props extends Omit<InputProps, "placeholder"> {
    name: string;
    isRequired?: boolean;
    acceptedFileTypes?: string[];
    className?: string;
}

export const FileUploadField = ({
    label,
    labelVisible = true,
    description,
    name,
    isRequired = false,
    acceptedFileTypes,
    className,
}: Props) => {
    const [file, setFile] = useState<File | undefined>();
    const inputRef = useRef<HTMLInputElement>(null);

    const formValidationState = useFormValidationState({
        value: undefined,
        validationBehavior: "native",
    });

    useFormValidation(
        { validationBehavior: "native" },
        formValidationState,
        inputRef,
    );

    const handleButtonClick = useCallback(() => {
        if (!inputRef.current) return;
        if (inputRef.current.value !== "") inputRef.current.value = "";
        inputRef.current.click();
    }, []);

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setFile(e.target.files?.[0]);
    }, []);

    return (
        <div className={className}>
            <InputContext.Provider
                value={{
                    name,
                    "required": isRequired,
                    "accept": acceptedFileTypes?.join(","),
                    "ref": inputRef,
                    "aria-invalid":
                        formValidationState.displayValidation.isInvalid,
                }}
            >
                <FieldErrorContext.Provider
                    value={formValidationState.displayValidation}
                >
                    <Label
                        className={concatClassNames([
                            labelVisible ? styles.label : undefined,
                            isRequired ? styles.required : undefined,
                        ])}
                    >
                        {labelVisible ? (
                            label
                        ) : (
                            <VisuallyHidden>{label}</VisuallyHidden>
                        )}
                    </Label>
                    <div className={styles.fileUploadRoot}>
                        <ButtonPrimary onPress={handleButtonClick}>
                            {typeof file !== "undefined"
                                ? t`Choose Different File`
                                : t`Choose File`}
                        </ButtonPrimary>
                        <Input
                            style={{ display: "none" }}
                            type="file"
                            onChange={handleChange}
                        />
                        {description && (
                            <Text
                                className={styles.description}
                                slot="description"
                            >
                                {description}
                            </Text>
                        )}
                        {file && <div>{file.name}</div>}
                    </div>
                    <FieldError className={styles.errorMessage}>
                        {({ validationErrors }) => validationErrors.join(", ")}
                    </FieldError>
                </FieldErrorContext.Provider>
            </InputContext.Provider>
        </div>
    );
};
